import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    CircularProgress,
    Alert,
    IconButton,
    Breadcrumbs,
    Link,
    Divider,
    Chip,
} from '@mui/material';
import { ArrowBack, Edit, AttachMoney, Inventory, Info, Label, Star, Category, SubdirectoryArrowRight } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getProductById } from '../../services/Api';

const ProductDetails = () => {
    const { subCategoryId, productId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const subCategoryName = location.state?.subCategoryName || 'Subcategory';
    const categoryName = location.state?.categoryName || 'Category';
    const categoryId = location.state?.categoryId;

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchProduct = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getProductById(productId);
            if (response.success) {
                setProduct(response.data);
            } else {
                setError(response?.message || 'Failed to fetch product details.');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching product details.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (location.state?.product) {
            setProduct(location.state.product);
            setLoading(false);
        } else {
            fetchProduct();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (error || !product) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">{error || 'Product not found.'}</Alert>
                <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>Back</Button>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate('/categories')} sx={{ cursor: 'pointer' }}>Categories</Link>
                    <Link underline="hover" color="inherit" onClick={() => navigate(`/categories/${categoryId}/items`, { state: { categoryName } })} sx={{ cursor: 'pointer' }}>{categoryName}</Link>
                    <Link underline="hover" color="inherit" onClick={() => navigate(`/sub-categories/${subCategoryId}/products`, { state: { subCategoryName, categoryName, categoryId } })} sx={{ cursor: 'pointer' }}>{subCategoryName} Products</Link>
                    <Typography color="text.primary">Product Details</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate(`/sub-categories/${subCategoryId}/products`, { state: { subCategoryName, categoryName, categoryId } })}
                            sx={{ border: '1px solid', borderColor: 'text.border' }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 300 }}>
                                {product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Full specifications and metadata for this item
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {/* <Button
                            variant="outlined"
                            startIcon={<Search />}
                            onClick={() => navigate(`/sub-categories/${subCategoryId}/products/${productId}/variants`, { state: { productName: product.name, subCategoryName, categoryName, categoryId } })}
                            sx={{ px: 3 }}
                        >
                            Show Variants
                        </Button> */}
                        <Button
                            variant="contained"
                            startIcon={<Edit />}
                            onClick={() => navigate(`/products/modify/${subCategoryId}/${productId}`, { state: { product, subCategoryName, categoryName, categoryId } })}
                            sx={{ px: 4, backgroundColor: 'primary.main' }}
                        >
                            Edit Details
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={4}>
                {/* Left Side: Summary & Image */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper elevation={0} sx={{ p: 0, borderRadius: 2, border: '1px solid', borderColor: 'text.border', overflow: 'hidden' }}>
                        <Box
                            component="img"
                            src={product.image || (product.productImages && product.productImages[0])}
                            sx={{ width: '100%', height: 350, objectFit: 'cover' }}
                        />
                        <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Chip
                                    label={product.isActive ? "ACTIVE" : "HIDDEN"}
                                    size="small"
                                    sx={{
                                        backgroundColor: product.isActive ? 'primary.main' : 'grey.400',
                                        color: 'white',
                                        fontWeight: 700,
                                        px: 1,
                                        borderRadius: '4px'
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary">ID: {product.id}</Typography>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Category sx={{ color: 'text.secondary', mr: 1.5, fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, width: 100 }}>Category:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{categoryName}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <SubdirectoryArrowRight sx={{ color: 'text.secondary', mr: 1.5, fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, width: 100 }}>Subcategory:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{subCategoryName}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Star sx={{ color: 'text.secondary', mr: 1.5, fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, width: 100 }}>Features:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {product.featureIds && product.featureIds.length > 0 ? product.featureIds.join(', ') : 'None'}
                                    </Typography>
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <AttachMoney sx={{ color: 'text.secondary', mr: 1.5, fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, width: 100 }}>Price:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {product.discountPrice ? (
                                            <>
                                                {product.discountPrice} <Typography component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '0.8rem', ml: 1 }}>{product.price}</Typography>
                                                {product.discountPercentage && <Chip size="small" label={`-${product.discountPercentage}%`} sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} color="error" />}
                                            </>
                                        ) : (
                                            `${product.price || 0}`
                                        )}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Inventory sx={{ color: 'text.secondary', mr: 1.5, fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, width: 100 }}>Stock:</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{product.stockQuantity || 0}</Typography>
                                        {product.stockQuantity <= (product.lowStockAlert || 5) && (
                                            <Chip size="small" label="Low Stock" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Side: Detailed Content */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
                        <Box sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Info sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Product Information</Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2.5,
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    minHeight: 120,
                                    '& p': { m: 0, mb: 1 },
                                    '& ul, & ol': { m: 0, pl: 3 }
                                }}
                                dangerouslySetInnerHTML={{ __html: product.information || 'No information provided.' }}
                            />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Label sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Key Highlights</Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2.5,
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    minHeight: 100,
                                    '& p': { m: 0, mb: 1 },
                                    '& ul, & ol': { m: 0, pl: 3 }
                                }}
                                dangerouslySetInnerHTML={{ __html: product.highlights || 'No highlights provided.' }}
                            />
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        {/* Variants Summary */}
                        {/* <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Inventory sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Variants Summary</Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">Showing {product.variants?.length || 0} variants</Typography>
                            </Box>
                            
                            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Table size="small">
                                    <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Stock</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(product.variants || []).slice(0, 5).map((v, i) => (
                                            <TableRow key={i} hover>
                                                <TableCell sx={{ fontWeight: 600 }}>{v.variantLabel}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <AttachMoney sx={{ fontSize: 14 }} />
                                                        {v.discountPrice || v.price}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={v.stockQuantity} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: v.isActive ? 'success.main' : 'error.main' }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {(!product.variants || product.variants.length === 0) && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No variants available</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box> */}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProductDetails;
